import { configureStore, ThunkAction, Action } from '@reduxjs/toolkit';

import type { RootState } from './root-state';
export type { RootState };

import featureFlags from './slices/feature-flags';
import layoutGrid from './slices/layout-grid';
import user from './slices/user';
import project from './slices/project';
import projects from './slices/projects';
import templates from './slices/templates';
import speakers from './slices/speakers';
import categories from './slices/categories';
import channels from './slices/channels';
import channel from './slices/channel';
import announcements from './slices/announcements';
import integrations from './slices/integrations';
import leaderboard from './slices/leaderboard';
import channelAdmins from './slices/channel-admins';
import superAdminNotifications from './slices/super-admin-notifications';
import access from './slices/access';
import passcodeLists from './slices/passcode-lists';
import registrants from './slices/registrants';
import audiences from './slices/audiences';
import channelSettings from './slices/channel-settings';
import stats from './slices/stats';
import series from './slices/series';
import content from './slices/content';
import stylepacks from './slices/stylepacks';
import campaigns from './slices/campaigns';
import campaignTemplates from './slices/campaign-templates';
import scheduledEmails from './slices/scheduled-emails';

import saveProjectMiddleware from './slices/project/middleware/save-project-middleware';
import saveProjectByFieldsMiddleware from './slices/project/middleware/save-project-by-fields-middleware';
import saveSessionMiddleware from './slices/project/middleware/save-session-middleware';
import loadProjectErrorMiddleware from './slices/project/middleware/load-project-error-middleware';
import connectedAdmins from './slices/connected-admins';
import channelAnalytics from './slices/channel-analytics';
import activityLog from './slices/activity-log';
import watermarks from './slices/watermarks';
import liveblocks from './slices/liveblocks';
import btvChannelCategories from './slices/btv-channel-categories';
import btvMedia from './slices/btv-media';

const reducerMap = {
  featureFlags: featureFlags,
  layoutGrid: layoutGrid,
  user,
  project,
  projects,
  templates,
  speakers,
  categories,
  channels,
  channel,
  announcements,
  integrations,
  leaderboard,
  connectedAdmins,
  channelAdmins,
  superAdminNotifications,
  access,
  passcodeLists,
  registrants,
  audiences,
  channelSettings,
  stats,
  series,
  channelAnalytics,
  content,
  stylepacks,
  activityLog,
  liveblocks,
  campaigns,
  campaignTemplates,
  scheduledEmails,
  watermarks,
  btvChannelCategories,
  btvMedia,
};

export const store = configureStore({
  reducer: reducerMap,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['content/createUploadJob'],
        ignoredPaths: ['content.uploadJobs'],
      },
    }).prepend(
      saveProjectMiddleware.middleware,
      saveProjectByFieldsMiddleware.middleware,
      saveSessionMiddleware.middleware,
      loadProjectErrorMiddleware.middleware
    );
  },
});

export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;

// Compile-time guard: TypeScript errors here if reducerMap and RootState diverge.
// If you add a slice to reducerMap, you must also update RootState in root-state.ts.
void (true satisfies [ReturnType<typeof store.getState>] extends [RootState]
  ? [RootState] extends [ReturnType<typeof store.getState>]
    ? true
    : 'RootState is missing slices present in reducerMap — update src/store/root-state.ts'
  : 'RootState has extra entries not in reducerMap — update src/store/root-state.ts');
